import type { GateKind } from "@/lib/gate";
import { ROUTES } from "@/lib/routes";

/**
 * The four places an account keeps, as a visitor sees them: each one's
 * words, read by the page (components/site/previews) and by its static
 * head and markdown copy (src/static), so the two never say different
 * things. Pure.
 */

export type PreviewKind = "saved" | "applications" | "resume" | "envoy";

export interface PreviewCopy {
    path: string;
    /** The place's name, over the title. */
    place: string;
    title: string;
    line: string;
    /** The one action, and the sheet it opens. */
    action: string;
    gate: GateKind;
    /** The same action again under the example, in other words, when the place asks twice. */
    closing?: string;
    /** What the example shows, said in the label over it. */
    exampleOf: string;
    /** The page's description, for search engines and link previews. */
    description: string;
}

export const PREVIEW_KINDS: readonly PreviewKind[] = ["saved", "applications", "resume", "envoy"];

export const PREVIEW_COPY: Record<PreviewKind, PreviewCopy> = {
    resume: {
        path: ROUTES.RESUME,
        place: "Resume",
        title: "One resume. Ready for each role.",
        line: "Add yours once. The extension fills any application from it for free, or tailors it to the posting first and answers the form's questions too. Nothing is added that you have not done, and you press Submit.",
        action: "Add my resume",
        gate: "upload",
        closing: "Get my resume ready",
        exampleOf: "An example job's application, filled both ways.",
        description:
            "Add your resume once. Autofill applications from it for free, or tailor it to each posting and have the questions answered too.",
    },
    applications: {
        path: ROUTES.APPLICATIONS,
        place: "Applications",
        title: "Every application, ready when you are.",
        line: "Each job you pick gets its own tailored resume and waits here. You review and send. Nothing goes without you.",
        action: "Track my applications",
        gate: "join",
        exampleOf: "Example applications.",
        description:
            "Every application in one place, from tailoring to sent. Each job gets its own resume, and you review and send each one.",
    },
    saved: {
        path: ROUTES.SAVED,
        place: "Saved",
        title: "Keep a shortlist.",
        line: "Save jobs as you browse. See which are still open, which are being tailored, and which have closed.",
        action: "Create my shortlist",
        gate: "join",
        exampleOf: "An example shortlist.",
        description:
            "Save jobs as you browse: a shortlist that shows which are still open, which are being tailored and which have closed.",
    },
    envoy: {
        path: ROUTES.ENVOY,
        place: "Envoy",
        title: "An envoy that looks while you are away.",
        line: "Say what you want once. It finds new matches every day, emails them each morning, and can tailor each one for you to send.",
        action: "Turn on my envoy",
        gate: "envoy",
        exampleOf: "An example envoy.",
        description:
            "Tell your envoy what you want once. It finds new matches every day, emails them each morning, and can tailor each one.",
    },
};
